import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useChunkedUpload, formatBytes, formatDuration } from '../hooks/useChunkedUpload';

interface FileItem {
  id: string;
  name: string;
  mainCategory: string;
  subCategory: string;
  url: string;
  downloadUrl: string;
  version: number;
  createdAt: string;
}

interface Building {
  id: string;
  name: string;
  buildingType: string;
  yearBuilt: number;
  grossFloorArea: number;
  heatingType: string;
  description: string;
  navvisUrl: string;
  cafmUrl: string;
  files: FileItem[];
}

interface Plot {
  id: string;
  name: string;
  parcelNumber: string;
  areaM2: number;
  landRegistry: string;
  description: string;
  project: {
    id: string;
    name: string;
    latitude: number;
    longitude: number;
    street: string;
    city: string;
    zipCode: string;
  };
  buildings: Building[];
  files: FileItem[];
}

const MAIN_CATEGORY_COLOR: Record<string, string> = {
  POINTCLOUD: '#e74c3c', MODEL_3D: '#3498db',
  DRAWING_2D: '#2ecc71', DOCUMENT: '#f39c12', OTHER: '#95a5a6',
}
const MAIN_CATEGORY_LABEL: Record<string, string> = {
  POINTCLOUD: 'Punktwolken', MODEL_3D: '3D-Modelle',
  DRAWING_2D: '2D-Pläne', DOCUMENT: 'Dokumente', OTHER: 'Sonstiges',
}
const SUB_CATEGORY_LABEL: Record<string, string> = {
  SCAN_RAW: 'Scandaten', BIM: 'BIM-Modell', MESH: 'Mesh',
  FLOOR_PLAN: 'Grundriss', SECTION: 'Schnitt', ELEVATION: 'Ansicht',
  ORTHOPHOTO: 'Orthofoto', AREA_BOOK: 'Raumbuch / Flächenbuch',
  SITE_PLAN: 'Lageplan / Flurkarte', RESCUE_PLAN: 'Feuer- & Rettungsplan',
  PHOTO: 'Fotos', RENDERING: 'Renderings', VIDEO: 'Videos',
  AREA_CALC: 'Flächenauswertung', CONDITION: 'Gebäudezustandsbewertung',
  FIRE_SAFETY: 'Brandschutzkonzept', HAZMAT: 'Schadstoffsanierung', REPORT: 'Berichte',
}

const ALL_WIDGETS = [
  { id: 'karte', label: 'Flurstück-Karte' },
  { id: 'gebaeude', label: 'Gebäude-Übersicht' },
  { id: 'dateien', label: 'Datenstatus' },
]
const DEFAULT_WIDGETS = ['karte', 'gebaeude', 'dateien']

const getFileExt = (name: string) => name.split('.').pop()?.toLowerCase() || ''
const canPreview = (file: FileItem): string | null => {
  const ext = getFileExt(file.name)
  if (ext === 'pdf') return 'pdf'
  if (['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext)) return 'image'
  if (['mp4', 'webm', 'mov'].includes(ext)) return 'video'
  return null
}

// ─── NRW ALKIS WFS helper ──────────────────────────────────────────────────

const buildAlkisKennzeichen = (raw: string): string => {
  // User can enter in different formats:
  // "053066-43-321" or "053066043000321______" or just "321" 
  // We try to detect the format
  const cleaned = raw.trim().replace(/\s/g, '')

  // Already full kennzeichen (21 chars)
  if (cleaned.length === 21) return cleaned

  // Format: gemarkung-flur-flurstueck (e.g. "053066-43-321")
  const dashMatch = cleaned.match(/^(\d{6})-(\d+)-(\d+)$/)
  if (dashMatch) {
    const gemarkung = dashMatch[1].padStart(6, '0')
    const flur = dashMatch[2].padStart(3, '0')
    const zaehler = dashMatch[3].padStart(5, '0')
    return `${gemarkung}${flur}${zaehler}______`
  }

  return cleaned
}

const token = localStorage.getItem('token');
const user = JSON.parse(localStorage.getItem('user') || '{}');

const fetchParcelByKennzeichen = async (kennzeichen: string): Promise<any> => {
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/plots/alkis/parcel`,
    { params: { kennzeichen }, headers: { Authorization: `Bearer ${token}` } }
  )
  return res.data?.features?.length > 0 ? res.data : null
}

const fetchParcelByCoords = async (lat: number, lng: number): Promise<any> => {
  const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/plots/alkis/parcel`,
    { params: { lat, lng }, headers: { Authorization: `Bearer ${token}` } }
  )
  return res.data?.features?.length > 0 ? res.data : null
}

// ─── Component ────────────────────────────────────────────────────────────────

const PlotDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const mapInitialized = useRef(false);

  const [plot, setPlot] = useState<Plot | null>(null);
  const chunked = useChunkedUpload({
    entityParams: { plotId: id },
    onComplete: () => fetchPlot()
  });
  const [dragOver, setDragOver] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<FileItem | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [expandedMain, setExpandedMain] = useState<Record<string, boolean>>({});
  const [parcelStatus, setParcelStatus] = useState<'idle' | 'loading' | 'found' | 'notfound'>('idle');
  const [detectedKennzeichen, setDetectedKennzeichen] = useState('');
  const [showWidgetConfig, setShowWidgetConfig] = useState(false);
  const [activeWidgets, setActiveWidgets] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem('plot_widgets');
      return saved ? JSON.parse(saved) : DEFAULT_WIDGETS;
    } catch { return DEFAULT_WIDGETS; }
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const canUpload = !['CUSTOMER_VIEWER'].includes(user.role);
  const canDelete = ['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN', 'CUSTOMER_MANAGER'].includes(user.role);

  const fetchPlot = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/plots/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlot(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchPlot(); }, [id]); // eslint-disable-line

  // ─── Map init ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeWidgets.includes('karte')) return;
    if (map.current) return;
    
    // Wait for DOM
    const timer = setTimeout(() => {
      if (!mapContainer.current) return;
      map.current = new maplibregl.Map({
        container: mapContainer.current,
        style: 'https://basemaps.cartocdn.com/gl/positron-gl-style/style.json',
        center: [6.7780319, 51.4360495],
        zoom: 16
      });
      map.current.addControl(new maplibregl.NavigationControl(), 'top-right');
      mapInitialized.current = true;
    }, 100);
    
    return () => clearTimeout(timer);
  }, [activeWidgets]);

  // ─── Add parcel polygon to map ────────────────────────────────────────────────
  const addParcelToMap = useCallback((geojson: any) => {
    console.log('Features found:', geojson.features.map((f: any) => f.properties.flurstueckskennzeichen))
    if (!map.current) return;
    const addLayers = () => {
      if (map.current!.getSource('parcel')) {
        (map.current!.getSource('parcel') as maplibregl.GeoJSONSource).setData(geojson);
      } else {
        map.current!.addSource('parcel', { type: 'geojson', data: geojson });
        map.current!.addLayer({ id: 'parcel-fill', type: 'fill', source: 'parcel', paint: { 'fill-color': '#00c49a', 'fill-opacity': 0.25 } });
        map.current!.addLayer({ id: 'parcel-outline', type: 'line', source: 'parcel', paint: { 'line-color': '#00c49a', 'line-width': 2.5 } });
      }
      // Fit to bounds
      const feature = geojson.features[0];
      if (feature?.geometry?.coordinates?.[0]) {
        const coords = feature.geometry.coordinates[0];
        const bounds = coords.reduce((b: maplibregl.LngLatBounds, c: number[]) =>
          b.extend([c[0], c[1]]),
          new maplibregl.LngLatBounds(coords[0], coords[0])
        );
        map.current!.fitBounds(bounds, { padding: 40 });
      }
    };
    if (map.current.loaded()) addLayers();
    else map.current.on('load', addLayers);
  }, []);

  // ─── Load parcel ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!plot || !activeWidgets.includes('karte')) return;

    const load = async () => {
      setParcelStatus('loading');
      try {
        if (!plot.project?.latitude || !plot.project?.longitude) {
          setParcelStatus('notfound');
          return;
        }

        const geojson = await fetchParcelByCoords(plot.project.latitude, plot.project.longitude);

        if (geojson && geojson.features?.length > 0) {
          const k = geojson.features[0]?.properties?.flurstueckskennzeichen || '';
          setDetectedKennzeichen(k);

          // Flurstücknummer automatisch speichern wenn noch nicht gesetzt
          if (!plot.parcelNumber && k) {
            try {
              await axios.patch(`${process.env.REACT_APP_API_URL}/api/plots/${plot.id}`,
                { parcelNumber: k },
                { headers: { Authorization: `Bearer ${token}` } }
              );
            } catch (e) { /* silent */ }
          }

          addParcelToMap(geojson);
          setParcelStatus('found');
        } else {
          setParcelStatus('notfound');
          if (map.current) {
            const center = () => {
              map.current!.flyTo({ center: [plot.project.longitude, plot.project.latitude], zoom: 17 });
            };
            if (map.current.loaded()) center();
            else map.current.on('load', center);
          }
        }
      } catch (err) {
        console.error('Parcel loading failed:', err);
        setParcelStatus('notfound');
      }
    };

    // Wait for map to be available
    const timeout = setTimeout(load, 500);
    return () => clearTimeout(timeout);
  }, [plot, activeWidgets, addParcelToMap, token]); // eslint-disable-line

  // ─── Widget config ────────────────────────────────────────────────────────────
  const toggleWidget = (wid: string) => {
    const updated = activeWidgets.includes(wid)
      ? activeWidgets.filter(w => w !== wid)
      : [...activeWidgets, wid];
    setActiveWidgets(updated);
    localStorage.setItem('plot_widgets', JSON.stringify(updated));
  };

  const handleDeleteFile = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/files/${deleteConfirm.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeleteConfirm(null);
      fetchPlot();
    } catch (err) { console.error(err); }
  };

  const toggleMain = (cat: string) => setExpandedMain(prev => ({ ...prev, [cat]: !prev[cat] }));

  const grouped = (plot?.files || []).reduce((acc, file) => {
    const main = file.mainCategory || 'OTHER';
    if (!acc[main]) acc[main] = [];
    acc[main].push(file);
    return acc;
  }, {} as Record<string, FileItem[]>);

  const previewType = previewFile ? canPreview(previewFile) : null;

  if (!plot) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', color: '#888' }}>Laden...</div>
  );

  const totalFiles = plot.files.length + plot.buildings.reduce((sum, b) => sum + (b.files?.length || 0), 0);
  const totalBGF = plot.buildings.reduce((sum, b) => sum + (b.grossFloorArea || 0), 0);
  const withNavvis = plot.buildings.filter(b => b.navvisUrl).length;

  const parcelStatusLabel = {
    idle: '',
    loading: 'Flurstück wird gesucht...',
    found: detectedKennzeichen ? `Gefunden: ${detectedKennzeichen}` : 'Flurstück gefunden',
    notfound: 'Flurstück nicht gefunden – Koordinaten oder Flurstücknummer prüfen',
  }[parcelStatus];

  const parcelStatusColor = {
    idle: '#aaa', loading: '#f39c12', found: '#00c49a', notfound: '#e74c3c',
  }[parcelStatus];

  return (
    <div style={s.container}>
      <div style={s.sidebar}>
        <div style={{ padding: '0 16px 24px 16px', textAlign: 'center' }}>
          <img src="/portalis_logo.png" alt="Portalis" style={{ width: '160px' }} />
        </div>
        <nav style={s.nav}>
          <a href="/dashboard" style={s.navItem}>Startseite</a>
          <a href="/projects" style={s.navItemActive}>Liegenschaften</a>
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER', 'CUSTOMER_ADMIN'].includes(user.role) && (
            <a href="/users" style={s.navItem}>Benutzer</a>
          )}
          {['SUPER_ADMIN', 'ADMIN', 'MANAGER'].includes(user.role) && (
            <a href="/customers" style={s.navItem}>Kunden</a>
          )}
        </nav>
        <div style={s.userInfo}>
          <p style={s.userName}>{user.firstName} {user.lastName}</p>
          <p style={s.userRole}>{user.role}</p>
          <button style={s.logoutButton} onClick={() => { localStorage.clear(); window.location.href = '/login'; }}>Abmelden</button>
        </div>
      </div>

      <div style={s.main}>
        {/* Breadcrumb */}
        <div style={s.breadcrumb}>
          <a href="/projects" style={s.breadcrumbLink}>Liegenschaften</a>
          {plot.project && (
            <><span style={s.breadcrumbSep}> › </span>
            <a href={`/projects/${plot.project.id}`} style={s.breadcrumbLink}>{plot.project.name}</a></>
          )}
          <span style={s.breadcrumbSep}> › </span>
          <span style={s.breadcrumbCurrent}>{plot.name}</span>
        </div>

        {/* Header */}
        <div style={s.header}>
          <div>
            <h1 style={s.title}>{plot.name}</h1>
            <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
              {plot.parcelNumber && <span style={s.metaTag}>Flurstück {plot.parcelNumber}</span>}
              {plot.areaM2 && <span style={s.metaTag}>{plot.areaM2.toLocaleString('de-DE')} m²</span>}
              {plot.landRegistry && <span style={s.metaTag}>Grundbuch {plot.landRegistry}</span>}
            </div>
            {plot.description && <p style={s.description}>{plot.description}</p>}
          </div>
          <button style={s.configBtn} onClick={() => setShowWidgetConfig(!showWidgetConfig)}>⚙️</button>
        </div>

        {/* Widget config */}
        {showWidgetConfig && (
          <div style={s.widgetConfigPanel}>
            <p style={{ margin: '0 0 10px 0', fontWeight: '600', fontSize: '13px', color: '#0f2744' }}>Sichtbare Widgets</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {ALL_WIDGETS.map(w => (
                <button key={w.id} onClick={() => toggleWidget(w.id)} style={{
                  padding: '5px 12px', borderRadius: '20px', border: '1px solid',
                  borderColor: activeWidgets.includes(w.id) ? '#00c49a' : '#ddd',
                  backgroundColor: activeWidgets.includes(w.id) ? '#f0faf7' : 'white',
                  color: activeWidgets.includes(w.id) ? '#00c49a' : '#888',
                  cursor: 'pointer', fontSize: '12px'
                }}>
                  {activeWidgets.includes(w.id) ? '✓ ' : ''}{w.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Widgets */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'flex-start' }}>

          {/* Karte */}
          {activeWidgets.includes('karte') && (
            <div style={{ ...s.widget, minWidth: '340px', flex: '2' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <p style={{ ...s.widgetTitle, margin: 0 }}>Flurstück-Karte (NRW ALKIS)</p>
                {parcelStatusLabel && (
                  <span style={{ fontSize: '11px', color: parcelStatusColor }}>{parcelStatusLabel}</span>
                )}
              </div>
              <div ref={mapContainer} style={{ width: '100%', height: '240px', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#f5f5f5' }} />
              <p style={{ fontSize: '11px', color: '#aaa', margin: '6px 0 0 0' }}>
                Flurstück wird automatisch anhand der Liegenschaftskoordinaten ermittelt.
                {detectedKennzeichen && ` Kennzeichen: ${detectedKennzeichen}`}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: '1', minWidth: '200px' }}>

            {/* Gebäude-Übersicht */}
            {activeWidgets.includes('gebaeude') && (
              <div style={s.widget}>
                <p style={s.widgetTitle}>Gebäude-Übersicht</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  {[
                    { label: 'Gebäude', value: plot.buildings.length },
                    { label: 'BGF gesamt', value: totalBGF > 0 ? `${totalBGF.toLocaleString('de-DE')} m²` : '–' },
                    { label: 'Dateien', value: totalFiles },
                    { label: 'Mit NavVis', value: withNavvis > 0 ? `${withNavvis}` : '–' },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ fontSize: '12px', color: '#888' }}>{item.label}</span>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#333' }}>{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Datenstatus */}
            {activeWidgets.includes('dateien') && (
              <div style={s.widget}>
                <p style={s.widgetTitle}>Datenstatus</p>
                {plot.buildings.length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: '12px', margin: 0 }}>Keine Gebäude</p>
                ) : plot.buildings.map(b => {
                  const cats = new Set(b.files?.map(f => f.mainCategory) || []);
                  return (
                    <div key={b.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '11px', color: '#555', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.name}</span>
                      <div style={{ display: 'flex', gap: '3px' }}>
                        {['POINTCLOUD', 'MODEL_3D', 'DRAWING_2D', 'DOCUMENT'].map(cat => (
                          <div key={cat} style={{
                            width: '9px', height: '9px', borderRadius: '50%',
                            backgroundColor: cats.has(cat) ? MAIN_CATEGORY_COLOR[cat] : '#eee',
                          }} title={MAIN_CATEGORY_LABEL[cat]} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Upload */}
        {canUpload && (
          <div
            style={{ ...s.dropzone, borderColor: dragOver ? '#00c49a' : '#ddd', backgroundColor: dragOver ? '#f0faf7' : 'white' }}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); chunked.handleUpload(e.dataTransfer.files); }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={e => chunked.handleUpload(e.target.files)} />
            {chunked.uploadQueue.length > 0 ? (
              <div style={{ width: '100%', maxWidth: '700px', margin: '0 auto', textAlign: 'left' }}>
                {chunked.uploadQueue.map(item => {
                  const elapsed = item.startTime ? (Date.now() - item.startTime) / 1000 : 0;
                  const bytesUploaded = item.uploadedParts * item.partSize;
                  const speedBps = elapsed > 0 ? bytesUploaded / elapsed : 0;
                  const remainingBytes = item.file.size - bytesUploaded;
                  const etaSec = speedBps > 0 ? remainingBytes / speedBps : 0;
                  const statusLabel = { queued: '⏳ Wartend', awaiting: '❓ Warte auf Bestätigung…', uploading: `${item.percent}%`, done: '✓ Fertig', error: '✕ Fehler', cancelled: '⊘ Abgebrochen' }[item.status];
                  const barColor = { queued: '#aaa', awaiting: '#f39c12', uploading: '#00c49a', done: '#00c49a', error: '#e74c3c', cancelled: '#aaa' }[item.status];
                  return (
                    <div key={item.id} style={{ padding: '10px 0', borderBottom: '1px solid #eee' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ color: '#0f2744', fontSize: '13px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, marginRight: '12px' }}>{item.file.name}</span>
                        <span style={{ color: '#888', fontSize: '11px', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {formatBytes(item.file.size)} · {statusLabel}
                          {(item.status === 'uploading' || item.status === 'queued') && (
                            <button onClick={(e) => { e.stopPropagation(); chunked.cancelUpload(item.id); }} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '14px', padding: '0 2px', lineHeight: 1 }} title="Upload abbrechen">✕</button>
                          )}
                        </span>
                      </div>
                      <div style={{ backgroundColor: '#f0f0f0', borderRadius: '3px', height: '6px', overflow: 'hidden' }}>
                        <div style={{ width: `${item.percent}%`, backgroundColor: barColor, height: '6px', transition: 'width 0.3s' }} />
                      </div>
                      {item.status === 'uploading' && speedBps > 0 && (
                        <p style={{ margin: '4px 0 0 0', color: '#888', fontSize: '11px' }}>{item.uploadedParts}/{item.totalParts} Parts · {formatBytes(speedBps)}/s · noch {formatDuration(etaSec)}</p>
                      )}
                      {item.status === 'error' && item.errorMsg && (
                        <p style={{ margin: '4px 0 0 0', color: '#e74c3c', fontSize: '11px' }}>{item.errorMsg}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                <p style={{ fontSize: '28px', margin: '0 0 6px 0' }}>📁</p>
                <p style={{ color: '#555', margin: '0 0 4px 0', fontSize: '14px' }}>Grundstücksdokumente hochladen</p>
                <p style={{ color: '#aaa', margin: '0', fontSize: '12px' }}>z.B. Flurkarte, Lageplan, Grundbuchauszug</p>
              </>
            )}
          </div>
        )}

        {/* Plot files */}
        {Object.keys(grouped).length > 0 && (
          <div style={{ marginBottom: '24px' }}>
            <h2 style={s.sectionTitle}>Grundstücksdokumente</h2>
            {Object.entries(grouped).map(([mainCat, catFiles]) => (
              <div key={mainCat} style={s.categoryCard}>
                <div style={s.categoryHeader} onClick={() => toggleMain(mainCat)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={s.categoryToggle}>{expandedMain[mainCat] ? '▼' : '▶'}</span>
                    <span style={{ ...s.categoryBadge, backgroundColor: MAIN_CATEGORY_COLOR[mainCat] || '#95a5a6' }}>
                      {MAIN_CATEGORY_LABEL[mainCat] || mainCat}
                    </span>
                    <span style={s.categoryCount}>{catFiles.length} Datei(en)</span>
                  </div>
                </div>
                {expandedMain[mainCat] && catFiles.map(file => {
                  const preview = canPreview(file);
                  return (
                    <div key={file.id} style={s.fileItem}>
                      <div style={s.fileInfo}>
                        <p style={s.fileName}>{file.name}</p>
                        <p style={s.fileMeta}>
                          {SUB_CATEGORY_LABEL[file.subCategory] || file.subCategory} · {new Date(file.createdAt).toLocaleDateString('de-DE')}
                        </p>
                      </div>
                      <div style={s.fileActions}>
                        {preview && <button onClick={() => setPreviewFile(file)} style={s.viewBtn}>Öffnen</button>}
                        <a href={file.downloadUrl} target="_blank" rel="noreferrer" style={s.downloadBtn}>Download</a>
                        {canDelete && <button onClick={() => setDeleteConfirm(file)} style={s.deleteBtn}>🗑️</button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Buildings */}
        <h2 style={s.sectionTitle}>Gebäude</h2>
        {plot.buildings.length === 0 ? (
          <div style={s.empty}><p style={{ color: '#aaa', margin: '0' }}>Noch keine Gebäude angelegt</p></div>
        ) : (
          <div style={s.buildingGrid}>
            {plot.buildings.map(building => (
              <div key={building.id} style={s.buildingCard} onClick={() => window.location.href = `/buildings/${building.id}`}>
                <div style={s.buildingHeader}>
                  <h3 style={s.buildingTitle}>{building.name}</h3>
                  <span style={s.fileCount}>{building.files?.length || 0} Dateien</span>
                </div>
                <div style={s.buildingMeta}>
                  {building.buildingType && <span style={s.metaTagSm}>{building.buildingType}</span>}
                  {building.yearBuilt && <span style={s.metaTagSm}>Bj. {building.yearBuilt}</span>}
                  {building.grossFloorArea && <span style={s.metaTagSm}>{building.grossFloorArea.toLocaleString('de-DE')} m²</span>}
                </div>
                <div style={s.dataTags}>
                  {['POINTCLOUD', 'MODEL_3D', 'DRAWING_2D', 'DOCUMENT'].map(key => (
                    <span key={key} style={building.files?.some((f: any) => f.mainCategory === key) ? s.tagActive : s.tag}>
                      {key === 'POINTCLOUD' ? 'PW' : key === 'MODEL_3D' ? '3D' : key === 'DRAWING_2D' ? '2D' : 'DO'}
                    </span>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                  {building.navvisUrl && (
                    <a href={building.navvisUrl} target="_blank" rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      style={{ fontSize: '11px', color: '#3498db', textDecoration: 'none', padding: '2px 8px', border: '1px solid #3498db', borderRadius: '4px' }}>
                      NavVis
                    </a>
                  )}
                  {building.cafmUrl && (
                    <a href={building.cafmUrl} target="_blank" rel="noreferrer"
                      onClick={e => e.stopPropagation()}
                      style={{ fontSize: '11px', color: '#9b59b6', textDecoration: 'none', padding: '2px 8px', border: '1px solid #9b59b6', borderRadius: '4px' }}>
                      CAFM
                    </a>
                  )}
                </div>
                <div style={s.openBtn}>Öffnen →</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Preview Modal */}
      {previewFile && previewType && (
        <div style={s.modalOverlay} onClick={() => setPreviewFile(null)}>
          <div style={s.previewModal} onClick={e => e.stopPropagation()}>
            <div style={s.previewHeader}>
              <span style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>{previewFile.name}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <a href={previewFile.downloadUrl} target="_blank" rel="noreferrer" style={s.previewDownloadBtn}>Download</a>
                <button onClick={() => setPreviewFile(null)} style={s.previewCloseBtn}>✕</button>
              </div>
            </div>
            <div style={s.previewContent}>
              {previewType === 'pdf' && <iframe src={previewFile.downloadUrl} style={{ width: '100%', height: '100%', border: 'none' }} title={previewFile.name} />}
              {previewType === 'image' && <img src={previewFile.downloadUrl} alt={previewFile.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />}
              {previewType === 'video' && <video controls style={{ maxWidth: '100%', maxHeight: '100%' }}><source src={previewFile.downloadUrl} /></video>}
            </div>
          </div>
        </div>
      )}
      {/* Batch Confirm */}
            {chunked.batchConfirm && (
              <div style={s.modalOverlay}>
                <div style={{ ...s.modal, maxWidth: '720px', maxHeight: '85vh', overflow: 'auto' }}>
                  <p style={{ fontSize: '20px', fontWeight: 600, color: '#0f2744', margin: '0 0 8px 0', textAlign: 'left' }}>Upload-Bestätigung</p>
                  <p style={{ fontSize: '13px', color: '#666', margin: '0 0 16px 0', textAlign: 'left' }}>Triff alle Entscheidungen jetzt – der Upload läuft danach ohne Unterbrechung durch.</p>
                  {chunked.batchConfirm.items.filter(i => i.existingFile).length > 1 && (
                    <div style={{ background: '#f8fafc', padding: '10px', borderRadius: '6px', marginBottom: '12px', textAlign: 'left', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '12px', color: '#475569' }}>Alle Konflikte:</span>
                      <button onClick={() => chunked.applyBulkDecision('version')} style={{ padding: '4px 10px', fontSize: '12px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>Neue Version</button>
                      <button onClick={() => chunked.applyBulkDecision('overwrite')} style={{ padding: '4px 10px', fontSize: '12px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>Überschreiben</button>
                      <button onClick={() => chunked.applyBulkDecision('skip')} style={{ padding: '4px 10px', fontSize: '12px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}>Überspringen</button>
                    </div>
                  )}
                  <div style={{ marginBottom: '16px', textAlign: 'left' }}>
                    {chunked.batchConfirm.items.map(item => {
                      const decision = chunked.batchDecisions[item.itemId] || (item.existingFile ? 'version' : 'new');
                      const isLarge = item.file.size > 1024 * 1024 * 1024;
                      return (
                        <div key={item.itemId} style={{ padding: '10px 12px', borderBottom: '1px solid #eee', background: decision === 'skip' ? '#fafafa' : 'white', opacity: decision === 'skip' ? 0.6 : 1 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <span style={{ fontWeight: 600, color: '#0f2744', fontSize: '13px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '70%' }}>{item.file.name}</span>
                            <span style={{ color: '#888', fontSize: '12px' }}>{formatBytes(item.file.size)}{isLarge && ' 🔥'}</span>
                          </div>
                          {item.existingFile ? (
                            <>
                              <p style={{ fontSize: '11px', color: '#999', margin: '0 0 6px 0' }}>V{item.existingFile.version} existiert{item.existingFile.size && <> ({formatBytes(item.existingFile.size)})</>}</p>
                              <div style={{ display: 'flex', gap: '14px', fontSize: '12px' }}>
                                <label style={{ cursor: 'pointer' }}><input type="radio" checked={decision === 'version'} onChange={() => chunked.setBatchDecisions(d => ({ ...d, [item.itemId]: 'version' }))} /> Als V{item.existingFile.version + 1}</label>
                                <label style={{ cursor: 'pointer' }}><input type="radio" checked={decision === 'overwrite'} onChange={() => chunked.setBatchDecisions(d => ({ ...d, [item.itemId]: 'overwrite' }))} /> Überschreiben</label>
                                <label style={{ cursor: 'pointer' }}><input type="radio" checked={decision === 'skip'} onChange={() => chunked.setBatchDecisions(d => ({ ...d, [item.itemId]: 'skip' }))} /> Überspringen</label>
                              </div>
                            </>
                          ) : (
                            <div style={{ display: 'flex', gap: '14px', fontSize: '12px' }}>
                              <label style={{ cursor: 'pointer' }}><input type="radio" checked={decision === 'new'} onChange={() => chunked.setBatchDecisions(d => ({ ...d, [item.itemId]: 'new' }))} /> Hochladen</label>
                              <label style={{ cursor: 'pointer' }}><input type="radio" checked={decision === 'skip'} onChange={() => chunked.setBatchDecisions(d => ({ ...d, [item.itemId]: 'skip' }))} /> Überspringen</label>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {(() => {
                    const willUpload = chunked.batchConfirm!.items.filter(it => { const d = chunked.batchDecisions[it.itemId] || (it.existingFile ? 'version' : 'new'); return d !== 'skip'; });
                    const totalBytes = willUpload.reduce((sum, it) => sum + it.file.size, 0);
                    const stats = chunked.batchConfirm!.storageStats;
                    const afterBytes = stats ? stats.customerUsedBytes + totalBytes : 0;
                    const usagePercent = stats ? Math.round((afterBytes / stats.serverTotalBytes) * 100) : 0;
                    const isWarning = usagePercent > 75;
                    return (
                      <div style={{ background: isWarning ? '#fff7ed' : '#f8fafc', borderLeft: isWarning ? '3px solid #f97316' : 'none', padding: '12px', borderRadius: '6px', textAlign: 'left' }}>
                        <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: '#475569' }}><strong>{willUpload.length}</strong> Dateien · <strong>{formatBytes(totalBytes)}</strong> gesamt</p>
                        {totalBytes > 100 * 1024 * 1024 && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: '#666' }}>Dauer (250 Mbps): ca. {formatDuration((totalBytes * 1.15) / ((250 * 1000000) / 8))}</p>}
                        {stats && <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: isWarning ? '#78350f' : '#666' }}>Speicher nach Upload: {formatBytes(afterBytes)} / {formatBytes(stats.serverTotalBytes)} ({usagePercent}%){isWarning && ' ⚠️'}</p>}
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                    <button onClick={() => chunked.batchConfirm!.resolve(null)} style={s.cancelBtn}>Alles abbrechen</button>
                    <button onClick={() => {
                      const finalDecisions = chunked.batchConfirm!.items.map(it => ({ itemId: it.itemId, mode: chunked.batchDecisions[it.itemId] || (it.existingFile ? 'version' as const : 'new' as const) }));
                      chunked.batchConfirm!.resolve(finalDecisions);
                    }} style={s.confirmMoveBtn}>Alle hochladen</button>
                  </div>
                </div>
              </div>
            )}
      {/* Delete Modal */}
      {deleteConfirm && (
        <div style={s.modalOverlay}>
          <div style={s.modal}>
            <p style={{ fontSize: '18px', fontWeight: '600', color: '#0f2744', margin: '0 0 8px 0' }}>Datei löschen</p>
            <p style={{ color: '#666', fontSize: '14px', margin: '0 0 24px 0' }}><strong>{deleteConfirm.name}</strong></p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)} style={s.cancelBtn}>Abbrechen</button>
              <button onClick={handleDeleteFile} style={s.confirmDeleteBtn}>Löschen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const s: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', height: '100vh', backgroundColor: '#f5f5f5' },
  sidebar: { width: '220px', backgroundColor: '#0f2744', display: 'flex', flexDirection: 'column', padding: '24px 0', flexShrink: 0 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#aac4e0', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', textDecoration: 'none', display: 'block' },
  navItemActive: { color: 'white', padding: '12px 24px', cursor: 'pointer', fontSize: '14px', backgroundColor: '#1e3a5f', borderLeft: '3px solid #00c49a', textDecoration: 'none', display: 'block' },
  userInfo: { padding: '24px', borderTop: '1px solid #1e3a5f' },
  userName: { color: 'white', margin: '0', fontSize: '14px', fontWeight: '600' },
  userRole: { color: '#00c49a', margin: '4px 0 12px 0', fontSize: '12px' },
  logoutButton: { backgroundColor: 'transparent', border: '1px solid #aac4e0', color: '#aac4e0', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', width: '100%' },
  main: { flex: 1, padding: '24px 32px', overflow: 'auto' },
  breadcrumb: { marginBottom: '16px', fontSize: '14px' },
  breadcrumbLink: { color: '#00c49a', textDecoration: 'none' },
  breadcrumbSep: { color: '#aaa', margin: '0 8px' },
  breadcrumbCurrent: { color: '#666' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px', gap: '16px' },
  title: { fontSize: '26px', color: '#0f2744', margin: '0 0 4px 0' },
  metaTag: { backgroundColor: '#f0f0f0', color: '#666', padding: '3px 10px', borderRadius: '20px', fontSize: '12px' },
  metaTagSm: { backgroundColor: '#f0f0f0', color: '#666', padding: '2px 6px', borderRadius: '4px', fontSize: '11px' },
  description: { color: '#888', margin: '8px 0 0 0', fontSize: '14px' },
  configBtn: { backgroundColor: 'white', border: '1px solid #ddd', color: '#666', padding: '8px 10px', borderRadius: '8px', cursor: 'pointer', fontSize: '16px', flexShrink: 0 },
  widgetConfigPanel: { backgroundColor: 'white', borderRadius: '10px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '12px' },
  widget: { backgroundColor: 'white', borderRadius: '12px', padding: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' },
  widgetTitle: { margin: '0 0 12px 0', fontSize: '11px', fontWeight: '600', color: '#888', textTransform: 'uppercase' as const, letterSpacing: '0.5px' },
  dropzone: { border: '2px dashed', borderRadius: '12px', padding: '24px', textAlign: 'center', cursor: 'pointer', marginBottom: '20px', transition: 'all 0.2s' },
  sectionTitle: { fontSize: '16px', color: '#0f2744', margin: '0 0 12px 0', fontWeight: '600' },
  categoryCard: { backgroundColor: 'white', borderRadius: '12px', marginBottom: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' },
  categoryHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 20px', cursor: 'pointer' },
  categoryToggle: { color: '#aaa', fontSize: '11px' },
  categoryBadge: { color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '13px', fontWeight: '600' },
  categoryCount: { color: '#888', fontSize: '13px' },
  fileItem: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderTop: '1px solid #f0f0f0' },
  fileInfo: { flex: 1, minWidth: 0 },
  fileName: { margin: '0', color: '#333', fontSize: '14px', fontWeight: '500', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileMeta: { margin: '2px 0 0 0', color: '#aaa', fontSize: '12px' },
  fileActions: { display: 'flex', gap: '6px', flexShrink: 0 },
  viewBtn: { border: '1px solid #3498db', color: '#3498db', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', backgroundColor: 'transparent', cursor: 'pointer' },
  downloadBtn: { border: '1px solid #00c49a', color: '#00c49a', padding: '5px 10px', borderRadius: '6px', fontSize: '12px', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' },
  deleteBtn: { border: '1px solid #e74c3c', color: '#e74c3c', padding: '5px 8px', borderRadius: '6px', fontSize: '12px', backgroundColor: 'transparent', cursor: 'pointer' },
  empty: { textAlign: 'center', padding: '40px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '24px' },
  buildingGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '16px' },
  buildingCard: { backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', cursor: 'pointer', borderTop: '4px solid #00c49a' },
  buildingHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' },
  buildingTitle: { margin: '0', color: '#0f2744', fontSize: '15px' },
  fileCount: { backgroundColor: '#f0faf7', color: '#00c49a', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', whiteSpace: 'nowrap' },
  buildingMeta: { display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap' },
  dataTags: { display: 'flex', gap: '4px', marginBottom: '8px' },
  tag: { padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', backgroundColor: '#f0f0f0', color: '#ccc' },
  tagActive: { padding: '2px 6px', borderRadius: '4px', fontSize: '11px', fontWeight: '600', backgroundColor: '#00c49a', color: 'white' },
  openBtn: { color: '#00c49a', fontSize: '13px', textAlign: 'right' as const, marginTop: '8px' },
  modalOverlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', maxWidth: '420px', width: '90%', textAlign: 'center' },
  cancelBtn: { padding: '8px 20px', borderRadius: '6px', border: '1px solid #ddd', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px' },
  confirmDeleteBtn: { padding: '8px 20px', borderRadius: '6px', border: 'none', backgroundColor: '#e74c3c', color: 'white', cursor: 'pointer', fontSize: '14px' },
  previewModal: { backgroundColor: '#1a1a1a', borderRadius: '12px', width: '90vw', height: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  previewHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', backgroundColor: '#0f0f0f', gap: '12px' },
  previewContent: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', padding: '8px' },
  previewDownloadBtn: { border: '1px solid #aac4e0', color: '#aac4e0', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', textDecoration: 'none' },
  previewCloseBtn: { border: 'none', background: 'none', color: 'white', cursor: 'pointer', fontSize: '18px', padding: '0 4px' },
};

export default PlotDetail;
